import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@mantine/core';
import { toStringId } from '../utils';
import { getAuthorityStatusBadgeColor, getInspectionStatusBadgeColor } from '../constants';

const useFiscalizacionColumns = ({
	handleClickEdit,
	handleClickDelete,
	handleClickIncumplimientos,
	handleClickFile,
	isCentroRole,
}) => {
	const columns = useMemo(
		() => [
			{
				header: 'Acciones',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'actions',
				columnDefType: 'display',
				size: 'auto',
				Cell: cellProps => {
					const rowData = cellProps.row.original;
					const incumplimientos = rowData?.incumplimientos || [];

					return (
						<React.Fragment>
							<div className='d-flex justify-content-center'>
								<div className='flex-shrink-0'>
									<ul className='list-inline mb-0'>
										<li className={`list-inline-item ${isCentroRole ? 'disabled' : ''}`}>
											<Link
												title='Editar'
												to='#'
												onClick={e => {
													if (isCentroRole) {
														e.preventDefault();
													} else {
														handleClickEdit(rowData);
													}
												}}
												aria-disabled={isCentroRole}
												className={isCentroRole ? 'text-muted' : ''}
											>
												<i className='ri-pencil-fill align-bottom me-2 text-muted'></i>
											</Link>
										</li>
										<li className={`list-inline-item ${!rowData.file_id && !rowData.finding_closure_file_id ? 'disabled' : ''}`}>
											<Link
												title='Ver Documento'
												to='#'
												onClick={e => {
													if (!rowData.file_id && !rowData.finding_closure_file_id) {
														e.preventDefault();
													} else {
														handleClickFile(rowData);
													}
												}}
												aria-disabled={!rowData.file_id && !rowData.finding_closure_file_id}
												className={!rowData.file_id && !rowData.finding_closure_file_id ? 'text-muted' : ''}
											>
												<i className='ri-file-search-line align-bottom me-2 text-muted'></i>
											</Link>
										</li>
										<li className='list-inline-item'>
											<Link
												title='Incumplimientos'
												to='#'
												onClick={() => handleClickIncumplimientos(rowData)}
											>
												<i
													className={`ri-list-check-2 align-bottom me-2 ${
														incumplimientos.length > 0 ? 'text-warning' : 'text-muted'
													}`}
												></i>
											</Link>
										</li>
										<li className={`list-inline-item ${isCentroRole ? 'disabled' : ''}`}>
											<Link
												title='Eliminar'
												to='#'
												className={`remove-item-btn ${isCentroRole ? 'text-muted' : ''}`}
												onClick={e => {
													if (isCentroRole) {
														e.preventDefault();
													} else {
														handleClickDelete(rowData);
													}
												}}
												aria-disabled={isCentroRole}
											>
												<i className='ri-delete-bin-line align-bottom me-2 text-muted'></i>
											</Link>
										</li>
									</ul>
								</div>
							</div>
						</React.Fragment>
					);
				},
			},
			{
				header: 'Código',
				dataType: 'string',
				accessorKey: 'inspection_code',
				Cell: cellProps => {
					const row = cellProps.row.original;
					const code =
						row.codigo ||
						row.code ||
						row.inspection_code ||
						(toStringId(row._id)
							? `FISC-${toStringId(row._id).slice(-6).toUpperCase()}`
							: '-');
					return <span style={{ textTransform: 'uppercase' }}>{code || '-'}</span>;
				},
			},
			{
				header: 'Fecha',
				dataType: 'date',
				accessorKey: 'inspection_date',
			},
			{
				header: 'Estado',
				dataType: 'select',
				accessorKey: 'inspection_status.name',
				Cell: cellProps => {
					const estado = cellProps.row.original.inspection_status?.name;
					if (!estado) return '-';
					return (
						<Badge color={getInspectionStatusBadgeColor(estado)} variant='light' tt='capitalize'>
							{estado}
						</Badge>
					);
				},
			},
			{
				header: 'N° Incumplimientos',
				dataType: 'number',
				accessorKey: 'breach_record_count',
			},
			{
				header: 'Respuesta Autoridad',
				dataType: 'select',
				accessorKey: 'worst_authority_status.name',
				Cell: cellProps => {
					const worst = cellProps.row.original.worst_authority_status;
					if (!worst?.name) return '-';
					return (
						<Badge color={getAuthorityStatusBadgeColor(worst.name)} variant='light' tt='capitalize'>
							{worst.name}
						</Badge>
					);
				},
			},
			{
				header: 'Tipo',
				dataType: 'multi-select',
				accessorKey: 'inspection_types.name',
			},
			{
				header: 'Responsable',
				dataType: 'select',
				accessorKey: 'person.name',
			},
			{
				header: 'Entidad Fiscalizadora',
				dataType: 'multi-select',
				accessorKey: 'inspection_authorities.name',
				Cell: cellProps => {
					const authorities = cellProps.row.original.inspection_authorities || [];
					if (authorities.length === 0) return '-';
					return (
						<div className='d-flex flex-wrap gap-1'>
							{authorities.map((auth, i) => (
								<Badge key={i} variant='light' tt='capitalize' size='xs'>
									{auth.name}
								</Badge>
							))}
						</div>
					);
				},
			},
			{
				header: 'Fiscalizadores',
				dataType: 'string',
				accessorKey: 'fiscalizer_names',
				Cell: cellProps => {
					const names = cellProps.row.original.fiscalizer_names || [];
					return names.length > 0 ? names.join(', ') : '-';
				},
			},
			{
				header: 'Establecimiento',
				dataType: 'multi-select',
				accessorKey: 'establishment.name',
			},
			{
				header: 'Observaciones',
				dataType: 'string',
				accessorKey: 'observation',
				Cell: cellProps => {
					const observaciones = cellProps.row.original.observation;
					if (!observaciones) return '-';
					return (
						<div
							style={{
								maxWidth: '300px',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}
							title={observaciones}
						>
							{observaciones}
						</div>
					);
				},
			},
			{
				header: 'Fecha de Creación',
				dataType: 'date',
				accessorKey: 'creation_date',
				visible: false,
				defaultSort: 'desc',
			},
		],
		[handleClickEdit, handleClickDelete, handleClickIncumplimientos, handleClickFile, isCentroRole]
	);

	return { columns };
};

export default useFiscalizacionColumns;
